import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { Input, FormField } from "@/design-system/components/ui";
import { PortalShell } from "../components/PortalShell";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";
import { usePortalAuth } from "../PortalAuthContext";
import { dashboardPathFor } from "./RecipientDashboardPage";
import type { ValidateInvitationResult } from "../api/types";

// External Intake & Collection v1.0 PR 4 §2.1 — magic-link landing.
// Sequence: validate(token) → sendOtp → verifyOtp → redeem.
// On success, refresh the portal session and navigate to the package
// (or to the index when more than one is in scope).

type Phase = "validating" | "needsOtp" | "redeeming" | "error";

export function RedeemPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams<{ token?: string }>();
  const auth = usePortalAuth();

  // Accept the token either as a path segment (canonical: /portal/r/abc)
  // or as a ?token= query string for tolerant copy-paste from email
  // clients that strip the path.
  const token = routeParams.token ?? searchParams.get("token") ?? "";

  const [phase, setPhase] = useState<Phase>("validating");
  const [info, setInfo] = useState<ValidateInvitationResult | null>(null);
  const [code, setCode] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase("error");
      setErrorMsg("This link is missing its access token. Open the link from your email exactly as we sent it.");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const result = await portalAuthApi.validate(token);
        if (cancelled) return;
        setInfo(result);
        // BUG C-2 — the account already has a password, so the email OTP is
        // redundant: send them straight to the normal sign-in (which, post
        // BUG C, surfaces the same packages). No interstitial / button —
        // there's nothing for the user to verify, so don't show this page.
        if (result.hasPassword) {
          navigate("/portal/login", { replace: true });
          return;
        }
        await portalAuthApi.sendOtp(token);
        if (cancelled) return;
        setPhase("needsOtp");
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setErrorMsg(err instanceof PortalApiError ? err.message : "Link is invalid or expired.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  async function handleVerify() {
    setErrorMsg(null);
    setBusy(true);
    try {
      await portalAuthApi.verifyOtp(token, code);
      setPhase("redeeming");
      const me = await portalAuthApi.redeem(token);
      auth.setMe(me);
      // Series 5a — a broker-owned redeem (applicant account-at-start)
      // lands on the broker dashboard, which renders the in-application
      // checklist + reviewer note. Remediation Tier 2 #9 (Prompt 9-ii) —
      // typed recipient redeems land on the per-type shell
      // (/portal/buyer | /seller | /vendor), which renders the shared
      // assignments / documents / materials / notifications modules.
      navigate(dashboardPathFor(me.accountType, me.kind), { replace: true });
    } catch (err) {
      setErrorMsg(err instanceof PortalApiError ? err.message : "Could not verify the code.");
      setPhase("needsOtp");
    } finally {
      setBusy(false);
    }
  }

  async function handleResendOtp() {
    setErrorMsg(null);
    setBusy(true);
    try {
      await portalAuthApi.sendOtp(token);
    } catch (err) {
      setErrorMsg(err instanceof PortalApiError ? err.message : "Could not resend the code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PortalShell pageTitle="Signing you in">
      <div className="flex flex-col gap-space-4">
        {phase === "validating" && (
          <p className="text-small text-gray-mid">Checking your link…</p>
        )}

        {(phase === "needsOtp" || phase === "redeeming") && info && (
          <div className="flex flex-col gap-space-3">
            <header>
              <h1 className="text-h2 font-display text-onyx m-0">Verify it's you</h1>
              <p className="text-small text-gray-mid mt-space-2 m-0">
                For your security, we send a one-time code to confirm this is the right person.
              </p>
            </header>
            <p className="text-small text-onyx">
              We sent a 6-digit code to <strong>{info.maskedContact}</strong>. Enter it below to continue.
            </p>
            <FormField label="Verification code" required>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                maxLength={8}
                disabled={busy || phase === "redeeming"}
              />
            </FormField>
            {errorMsg && <p className="text-small text-status-error m-0" role="alert">{errorMsg}</p>}
            <div className="flex flex-wrap items-center gap-space-2">
              <Button onClick={() => void handleVerify()} disabled={busy || phase === "redeeming" || code.length < 4}>
                {phase === "redeeming" ? "Signing you in…" : "Continue"}
              </Button>
              <button
                type="button"
                onClick={() => void handleResendOtp()}
                disabled={busy}
                className="text-small underline text-onyx"
              >
                Resend code
              </button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="flex flex-col gap-space-3">
            <p className="text-small text-status-error m-0" role="alert">{errorMsg}</p>
            <p className="text-small text-gray-mid m-0">
              If the link expired, ask the team to send a new one — your progress is saved.
            </p>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
